import { Box, Button, Skeleton, Stack, type SxProps, Typography } from '@mui/material'
import { ProgramTracker } from 'Components/Enrollment/ProgramTracker'
import { EnrollmentChip } from 'Components/Enrollment/EnrollmentChip'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import EventIcon from '@mui/icons-material/Event'
import BusinessIcon from '@mui/icons-material/Business'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import SubjectIcon from '@mui/icons-material/Subject'
import HistoryIcon from '@mui/icons-material/History'
import { useEffect, useState } from 'react'
import { useRefresh } from 'ContextProviders/RefreshProvider'
import { sendGet } from 'hooks/use-fetch'
import { NewEnrollmentStatus, ProgramVerificationStatus, type NewEnrollmentDetails, AccountType } from 'core/types'
import { FormatDateAsTimeAgo, FormatDateValue } from 'core/Utilities'
import CheckIcon from '@mui/icons-material/Check'
import S3Image from 'Components/S3Image'
import 'core/styles.css'
import { ProgramVerificationChip } from 'Components/Program/ProgramVerificationChip'
import { EnrollmentEdit } from 'Pages/Enrollments/EnrollmentEdit'
import { generatePath, useNavigate } from 'react-router'
import * as AppRoutes from 'Pages/Routes'
import { ListAlt } from '@mui/icons-material'
import { usePermissions } from 'hooks/use-permissions'
import { useAccountId } from 'ContextProviders/CurrentAccount'

export function EnrollmentDetails (props: { enrollmentID: number, canReview: boolean, readOnly?: boolean, fromChildID?: number }): JSX.Element {
  const [showEditModal, setShowEditModal] = useState(false)
  const [enrollmentData, setEnrollmentData] = useState<NewEnrollmentDetails | null>(null)
  const nav = useNavigate()
  const perms = usePermissions()
  const refresh = useRefresh()
  const account = useAccountId()
  const reviewEnrollment = (): void => {
    let route = generatePath(AppRoutes.ENROLLMENT, { id: props.enrollmentID.toString() })
    if (props.fromChildID != null) {
      route += `?childID=${props.fromChildID}`
    }
    nav(route)
  }
  const showEdit = (): void => {
    setShowEditModal(true)
  }
  const hideEdit = (): void => {
    setShowEditModal(false)
  }
  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response } = await sendGet(`/NewEnrollment/GetEnrollmentDetails?id=${props.enrollmentID}`, {})
      setEnrollmentData(response)
    }
    void loadData()
  }, [props.enrollmentID, refresh])
  const iconSize: SxProps = { height: '30px', width: '30px' }
  if (enrollmentData == null) { return <Skeleton variant='rectangular' /> }
  return <Box className='CardDisplay'>
    <Typography variant='h6' sx={{ fontWeight: 'bold' }}>Enrollment Details</Typography>
    <Typography variant='body1' sx={{ fontWeight: 'bold', overflowWrap: 'break-word' }} gutterBottom>{enrollmentData.districtName}</Typography>
    <Stack direction='column' spacing={1}>
      <Stack direction='row' spacing={1}>
        {enrollmentData.serviceS3Key != null
          ? <S3Image style={{ width: '30px', height: '30px' }} imageKey={enrollmentData.serviceS3Key} />
          : <SchoolOutlinedIcon />}
        <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>{enrollmentData.serviceName}</Typography>
      </Stack>
      {enrollmentData.programVerificationID != null && (
        <ProgramVerificationChip programName={enrollmentData.programName}
          status={enrollmentData.programVerificationStatus ?? ProgramVerificationStatus.NotVerified}
          timeSpanName={enrollmentData.timeSpanName ?? ''}
          showStatusName={true}
        />
      )}
      {enrollmentData.sessionName != null && <Stack direction='row' spacing={1}>
        <EventIcon />
        <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>{enrollmentData.sessionName}</Typography>
      </Stack>}
      {enrollmentData.isdspProgramID != null && enrollmentData.sessionID != null && <ProgramTracker programId={enrollmentData.isdspProgramID} sessionId={enrollmentData.sessionID} />}
      {enrollmentData.facilityName != null && <Stack direction='row' spacing={1}>
        <BusinessIcon />
        <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>{enrollmentData.facilityName ?? ''}</Typography>
      </Stack>}
      {(enrollmentData.desiredSchedule ?? '') !== '' && <Stack direction='row' spacing={1}>
        <EventAvailableIcon />
        <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>{enrollmentData.desiredSchedule}</Typography>
      </Stack>}
      {(enrollmentData.notes ?? '') !== '' && <Stack direction='row' spacing={1}>
        <SubjectIcon />
        <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>{enrollmentData.notes}</Typography>
      </Stack>}
      <Stack direction='row' spacing={1}>
        <CheckIcon />
        <EnrollmentChip statusId={enrollmentData.statusID} rank={enrollmentData.rank} showToolTip />
      </Stack>
      {enrollmentData.declinedOrExitedReason !== null && <Stack direction='row' spacing={1}>
        <ListAlt />
        <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>{enrollmentData.declinedOrExitedReason}</Typography>
      </Stack>}
      <Stack direction='row' spacing={1}>
        <HistoryIcon sx={iconSize} />
        <Stack direction='column'>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>Last Updated {FormatDateValue(enrollmentData.lastActionDate)}</Typography>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>({FormatDateAsTimeAgo(enrollmentData.lastActionDate)})</Typography>
        </Stack>
      </Stack>
      {(perms.Enrollment_Edit || perms.CareNetwork_Enrollment_Edit) && (enrollmentData.statusID <= NewEnrollmentStatus.Processing || (enrollmentData.statusID === NewEnrollmentStatus.Exited && account?.type === AccountType.ISD)) &&
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <EnrollmentEdit enrollmentId={props.enrollmentID} show={showEditModal} onClose={hideEdit} restrictedEdit={enrollmentData.statusID === NewEnrollmentStatus.Processing || enrollmentData.statusID === NewEnrollmentStatus.Exited} />
          <Button variant='contained' color='primary' onClick={showEdit} sx={{ textTransform: 'none', px: 4 }}>Edit Enrollment</Button>
        </Box>
      }
      {props.canReview && <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button onClick={reviewEnrollment} sx={{ textTransform: 'none', textDecoration: 'underline', color: 'black' }}>Review Enrollment</Button>
      </Box>}
    </Stack>
  </Box>
}
